/**
 * <discount-price> — renders a price with a percentage discount applied.
 *
 *   <discount-price
 *     data-price="300"            cents, the current price
 *     data-compare-at="0"         cents, optional compare-at price
 *     data-percent="100"          the discount to apply, 0–100
 *     data-quantity="1"           optional, reserved for quantity-based rules
 *     data-money-format="${{amount}}">
 *     <span data-component="price"></span>
 *     <span data-component="price-compare"></span>
 *   </discount-price>
 *
 * The discounted price goes into [data-component="price"], and the price it
 * replaces (compare-at, or the undiscounted price) into
 * [data-component="price-compare"] — empty when there is nothing to strike.
 *
 * Display only: this shows what the discount comes to, it does not create it.
 * The matching discount has to exist in Shopify (automatic discount, script or
 * function), otherwise the cart will charge the full price.
 */
(() => {
  if (customElements.get('discount-price')) return;

  const DEFAULT_FORMAT = '${{amount}}';

  function delimit(value, decimals, thousands, separator) {
    const parts = value.toFixed(decimals).split('.');
    return (
      parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) +
      (parts[1] ? separator + parts[1] : '')
    );
  }

  /** Mirrors Shopify's money_format placeholders. */
  function formatMoney(cents, format) {
    const value = cents / 100;
    return String(format || DEFAULT_FORMAT).replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return delimit(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return delimit(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return delimit(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return delimit(value, 2, "'", '.');
        default:
          return delimit(value, 2, ',', '.');
      }
    });
  }

  class DiscountPrice extends HTMLElement {
    static get observedAttributes() {
      return ['data-price', 'data-compare-at', 'data-percent', 'data-quantity'];
    }

    connectedCallback() {
      this.render();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      if (this.isConnected) this.render();
    }

    get percent() {
      const percent = parseFloat(this.dataset.percent);
      if (Number.isNaN(percent)) return 0;
      return Math.min(Math.max(percent, 0), 100);
    }

    render() {
      const base = parseInt(this.dataset.price, 10);
      if (Number.isNaN(base)) return;

      const compareAt = parseInt(this.dataset.compareAt, 10) || 0;
      const percent = this.percent;
      const format = this.dataset.moneyFormat;

      const final = Math.round((base * (100 - percent)) / 100);
      // Strike through the compare-at price, or the full price when discounted
      const strike = Math.max(compareAt, percent > 0 ? base : 0);

      this.querySelectorAll('[data-component="price"]').forEach((el) => {
        el.textContent = formatMoney(final, format);
      });

      this.querySelectorAll('[data-component="price-compare"]').forEach((el) => {
        el.textContent = strike > final ? formatMoney(strike, format) : '';
      });

      this.classList.toggle('price--on-sale', strike > final);
      this.dataset.appliedPercent = String(percent);
      this.classList.add('is-ready');
    }
  }

  customElements.define('discount-price', DiscountPrice);
})();
